// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { defaultFilter } from "cmdk";
import {
  Activity,
  Building2,
  ChevronLeft,
  Flag,
  Home,
  IdCard,
  Languages,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  Paintbrush,
  Palette,
  RefreshCw,
  Store,
  Sun,
  UserRoundPen,
  UserRoundPlus,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChainPlusIcon,
  DatabasePlusIcon,
  ImportPlusIcon,
  MemoryStickPlusIcon,
  SettingsCompareIcon,
  StorePlusIcon,
  SushiPlusIcon,
} from "@/components/icons";
import { carriedDestination, readCarryover } from "@/components/layout/detail-tab-carryover";
import { Badge } from "@/components/ui/badge";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CompareChainDialog } from "@/features/chains/compare-chains-dialog";
import { CreateChainDialog } from "@/features/chains/create-chain-dialog";
import { FeatureFlagsDialog } from "@/features/debug/feature-flags-dialog";
import { TelemetryDialog } from "@/features/debug/telemetry-dialog";
import { CreateFirmwareDialog } from "@/features/hardware/create-firmware-dialog";
import { CreateUserDialog } from "@/features/iam/create-user-dialog";
import { CreateVendorDialog } from "@/features/iam/create-vendor-dialog";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { CreateTransformationDialog } from "@/features/ingress/create-transformation-dialog";
import { CreateOctoRuleDialog } from "@/features/octo/create-octo-rule-dialog";
import { CompareStoreDialog } from "@/features/stores/compare-stores-dialog";
import { CreateStoreDialog } from "@/features/stores/create-store-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useRippleDelegated } from "@/hooks/use-ripple";
import { useAuth } from "@/lib/auth";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode, useTelemetry } from "@/lib/debug-mode";
import { NAV_GROUPS } from "@/lib/navigation";
import { useAccent } from "@/lib/preferences";
import { cn } from "@/lib/utils";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
] as const;

const THEMES = [
  { value: "light", icon: Sun },
  { value: "dark", icon: Moon },
  { value: "system", icon: Monitor },
] as const;

/* Keep in sync with the `[data-accent]` palettes in globals.css — see the note on
   ACCENT_SWATCHES in profile-settings-tab.tsx for why these are literal. */
const ACCENTS = [
  { value: "green", swatch: "bg-[oklch(0.612_0.146_157)]" },
  { value: "red", swatch: "bg-[oklch(0.575_0.12_22)]" },
  { value: "blue", swatch: "bg-[oklch(0.615_0.145_245)]" },
] as const;

type Page = "root" | "theme" | "language" | "accent";

/** Is a dialog or sheet already open? Checked only while the palette is closed, so any open overlay is
 * someone else's. `data-state="open"` skips overlays still animating out after closing. */
function isOtherModalOpen(): boolean {
  return document.querySelector('[data-slot$="-overlay"][data-state="open"]') !== null;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<Page>("root");
  const [search, setSearch] = useState("");
  const [flagsDialogOpen, setFlagsDialogOpen] = useState(false);
  const [telemetryDialogOpen, setTelemetryDialogOpen] = useState(false);
  const [compareChainOpen, setCompareChainOpen] = useState(false);
  const [compareStoreOpen, setCompareStoreOpen] = useState(false);
  const [createChainOpen, setCreateChainOpen] = useState(false);
  const [createStoreOpen, setCreateStoreOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createVendorOpen, setCreateVendorOpen] = useState(false);
  const [createTransformationOpen, setCreateTransformationOpen] = useState(false);
  const [createOctoRuleOpen, setCreateOctoRuleOpen] = useState(false);
  const [createFirmwareOpen, setCreateFirmwareOpen] = useState(false);
  const [importDataOpen, setImportDataOpen] = useState(false);
  const telemetry = useTelemetry();
  const inputRef = useRef<HTMLInputElement>(null);
  const [listEl, setListEl] = useState<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("back-office");
  const { logout, hasPermission } = useAuth();
  const { setTheme } = useTheme();
  const [, setAccent] = useAccent();
  const { debugMode } = useDebugMode();
  const queryClient = useQueryClient();
  const { data: ctx } = useBackofficeContext();

  useRippleDelegated(listEl);

  const MAX_SEARCH_RESULTS = 25;

  const matchingChains = useMemo(() => {
    if (!search || !ctx?.chains || !hasPermission(PAGE_PERMISSIONS.chains)) return [];
    return ctx.chains
      .map((c) => ({ item: c, score: defaultFilter(`${c.id} ${c.value} ${c.display}`, search) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_SEARCH_RESULTS)
      .map((r) => r.item);
  }, [search, ctx?.chains, hasPermission]);

  const matchingStores = useMemo(() => {
    if (!search || !ctx?.stores || !hasPermission(PAGE_PERMISSIONS.stores)) return [];
    return ctx.stores
      .map((s) => ({ item: s, score: defaultFilter(`${s.id} ${s.value} ${s.display}`, search) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_SEARCH_RESULTS)
      .map((r) => r.item);
  }, [search, ctx?.stores, hasPermission]);

  /* Jumping to a sibling from a chain/store detail or edit page keeps you on the page and tab you were on. */
  const carryover = readCarryover(location.pathname, location.search);

  // biome-ignore lint/correctness/useExhaustiveDependencies: search is intentionally used as a trigger
  useEffect(
    function scrollListToTopOnSearch() {
      listRef.current?.scrollTo(0, 0);
    },
    [search],
  );

  useEffect(
    function registerGlobalKeyboardShortcuts() {
      function onKeyDown(e: KeyboardEvent) {
        if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          // ⌘K always closes the palette, but never opens it over another modal — stacking the two
          // leaves focus trapped in whichever mounted last and Esc closing the wrong one.
          setOpen((prev) => (prev ? false : !isOtherModalOpen()));
        }
        if (e.key === "r" && e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          queryClient.refetchQueries({ type: "active" });
        }
      }
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    },
    [queryClient],
  );

  function handleOpenChange(value: boolean) {
    setOpen(value);
    if (!value) {
      setPage("root");
      setSearch("");
    }
  }

  function handleSelect(to: string, search?: { t: string }) {
    setOpen(false);
    navigate({ to, search });
  }

  function goBack() {
    setPage("root");
    setSearch("");
  }

  return (
    <>
      <CommandDialog open={open} onOpenChange={handleOpenChange}>
        <CommandInput
          ref={inputRef}
          placeholder={
            page === "theme"
              ? t("commandPalette.theme.placeholder")
              : page === "accent"
                ? t("commandPalette.accent.placeholder")
                : page === "language"
                  ? t("commandPalette.language.placeholder")
                  : t("commandPalette.placeholder")
          }
          value={search}
          onValueChange={setSearch}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !search && page !== "root") {
              e.preventDefault();
              goBack();
            }
          }}
        />
        <CommandList
          ref={(el: HTMLDivElement | null) => {
            listRef.current = el;
            setListEl(el);
          }}
        >
          {matchingChains.length === 0 && matchingStores.length === 0 && (
            <CommandEmpty>{t("commandPalette.empty")}</CommandEmpty>
          )}

          {page === "root" && (
            <>
              <CommandGroup className="pt-2">
                <CommandItem onSelect={() => handleSelect("/home")}>
                  <Home className="mr-2 h-4 w-4" />
                  {t("home.title")}
                </CommandItem>
              </CommandGroup>
              {NAV_GROUPS.map((group) => {
                const visibleItems = group.items.filter(
                  (item) => (!item.debugOnly || debugMode) && hasPermission(item.permission),
                );
                if (visibleItems.length === 0) return null;
                return (
                  <CommandGroup className="pt-2" key={group.labelKey} heading={t(group.labelKey)}>
                    {visibleItems.map((item) => (
                      <CommandItem
                        key={item.to}
                        value={`${t(group.labelKey)} ${t(item.labelKey)}`}
                        onSelect={() => handleSelect(item.to)}
                      >
                        <item.icon className="mr-2 h-4 w-4" />
                        {t(item.labelKey)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
              <CommandGroup className="pt-2" heading={t("commandPalette.commands")}>
                {hasPermission(PAGE_PERMISSIONS.chainsEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateChainOpen(true);
                    }}
                  >
                    <ChainPlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.createChain")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.storesEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateStoreOpen(true);
                    }}
                  >
                    <StorePlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.createStore")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.iamUsersEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateUserOpen(true);
                    }}
                  >
                    <UserRoundPlus className="mr-2 h-4 w-4" />
                    {t("commandPalette.createUser")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.iamVendorsEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateVendorOpen(true);
                    }}
                  >
                    <IdCard className="mr-2 h-4 w-4" />
                    {t("commandPalette.createVendor")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.ingressImportsEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setImportDataOpen(true);
                    }}
                  >
                    <ImportPlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.importData")}
                  </CommandItem>
                )}
                {debugMode && hasPermission(PAGE_PERMISSIONS.ingressTransformationsEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateTransformationOpen(true);
                    }}
                  >
                    <DatabasePlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.createTransformation")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.octoRulesEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateOctoRuleOpen(true);
                    }}
                  >
                    <SushiPlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.createOctoRule")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.hardwareFirmwareEdit) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateFirmwareOpen(true);
                    }}
                  >
                    <MemoryStickPlusIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.createFirmwareVersion")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.chains) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCompareChainOpen(true);
                    }}
                  >
                    <SettingsCompareIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.compareChainSettings")}
                  </CommandItem>
                )}
                {hasPermission(PAGE_PERMISSIONS.stores) && (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCompareStoreOpen(true);
                    }}
                  >
                    <SettingsCompareIcon className="mr-2 h-4 w-4" />
                    {t("commandPalette.compareStoreSettings")}
                  </CommandItem>
                )}
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    queryClient.refetchQueries({ type: "active" });
                  }}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("commandPalette.refreshView")}
                  <kbd className="ml-auto rounded border bg-muted px-1 font-mono text-[11px] text-muted-foreground">
                    Ctrl+R
                  </kbd>
                </CommandItem>
              </CommandGroup>
              <CommandGroup className="pt-2" heading={t("commandPalette.settings")}>
                <CommandItem
                  onSelect={() => {
                    setPage("theme");
                    setSearch("");
                  }}
                >
                  <Palette className="mr-2 h-4 w-4" />
                  {t("commandPalette.theme.title")}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setPage("accent");
                    setSearch("");
                  }}
                >
                  <Paintbrush className="mr-2 h-4 w-4" />
                  {t("commandPalette.accent.title")}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setPage("language");
                    setSearch("");
                  }}
                >
                  <Languages className="mr-2 h-4 w-4" />
                  {t("commandPalette.language.title")}
                </CommandItem>
              </CommandGroup>
              <CommandGroup className="pt-2" heading={t("commandPalette.profile")}>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    navigate({ to: "/profile" });
                  }}
                >
                  <UserRoundPen className="mr-2 h-4 w-4" />
                  {t("commandPalette.myProfile")}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    logout().finally(() => navigate({ to: "/login" }));
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("commandPalette.logout")}
                </CommandItem>
              </CommandGroup>
              {debugMode && (
                <CommandGroup className="pt-2" heading={t("commandPalette.debug")}>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setFlagsDialogOpen(true);
                    }}
                  >
                    <Flag className="mr-2 h-4 w-4" />
                    {t("commandPalette.featureFlags")}
                  </CommandItem>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setTelemetryDialogOpen(true);
                    }}
                  >
                    <Activity className="mr-2 h-4 w-4" />
                    {t("commandPalette.telemetry")}
                    {telemetry.length > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {telemetry.length}
                      </Badge>
                    )}
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect("/showcase")}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    {t("commandPalette.showcase")}
                  </CommandItem>
                </CommandGroup>
              )}
              {matchingChains.length > 0 && (
                <CommandGroup forceMount className="pt-2" heading={t("chains.title")}>
                  {matchingChains.map((chain) => (
                    <CommandItem
                      forceMount
                      key={chain.id}
                      value={`${chain.id} ${chain.value} ${chain.display}`}
                      onSelect={() => {
                        const { to, search } = carriedDestination(carryover, "chains", chain.value);
                        handleSelect(to, search);
                      }}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      {chain.display}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {matchingStores.length > 0 && (
                <CommandGroup forceMount className="pt-2" heading={t("stores.title")}>
                  {matchingStores.map((store) => (
                    <CommandItem
                      forceMount
                      key={store.id}
                      value={`${store.id} ${store.value} ${store.display}`}
                      onSelect={() => {
                        const { to, search } = carriedDestination(carryover, "stores", store.value);
                        handleSelect(to, search);
                      }}
                    >
                      <Store className="mr-2 h-4 w-4" />
                      {store.display}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}

          {page === "theme" && (
            <CommandGroup className="pt-2" heading={t("commandPalette.theme.title")}>
              <CommandItem onSelect={goBack}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                {t("commandPalette.back")}
              </CommandItem>
              {THEMES.map((theme) => (
                <CommandItem
                  key={theme.value}
                  onSelect={() => {
                    setOpen(false);
                    setTheme(theme.value);
                  }}
                >
                  <theme.icon className="mr-2 h-4 w-4" />
                  {t(`commandPalette.theme.${theme.value}`, theme.value)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {page === "accent" && (
            <CommandGroup className="pt-2" heading={t("commandPalette.accent.title")}>
              <CommandItem onSelect={goBack}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                {t("commandPalette.back")}
              </CommandItem>
              {ACCENTS.map((accent) => (
                <CommandItem
                  key={accent.value}
                  onSelect={() => {
                    setOpen(false);
                    setAccent(accent.value);
                  }}
                >
                  <span className="mr-2 flex h-4 w-4 items-center justify-center">
                    <span className={cn("size-3 rounded-full", accent.swatch)} />
                  </span>
                  {t(`profile.settings.accent.options.${accent.value}`)}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {page === "language" && (
            <CommandGroup className="pt-2" heading={t("commandPalette.language.title")}>
              <CommandItem onSelect={goBack}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                {t("commandPalette.back")}
              </CommandItem>
              {LANGUAGES.map((lang) => (
                <CommandItem
                  key={lang.code}
                  onSelect={() => {
                    setOpen(false);
                    i18n.changeLanguage(lang.code);
                  }}
                >
                  <span className="mr-2 flex h-4 w-4 items-center justify-center text-lg leading-none">
                    {lang.flag}
                  </span>
                  {lang.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        <div className="flex items-center gap-4 border-t px-3 py-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 font-mono">↑↓</kbd>
            {t("commandPalette.footer.navigate")}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 font-mono">↵</kbd>
            {t("commandPalette.footer.select")}
          </span>
          {page !== "root" && (
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1 font-mono">⌫</kbd>
              {t("commandPalette.footer.goBack")}
            </span>
          )}
          <span className="flex items-center gap-1 ml-auto">
            <kbd className="rounded border bg-muted px-1 font-mono">Esc</kbd>
            {t("commandPalette.footer.close")}
          </span>
        </div>
      </CommandDialog>

      <FeatureFlagsDialog open={flagsDialogOpen} onOpenChange={setFlagsDialogOpen} />
      <TelemetryDialog open={telemetryDialogOpen} onOpenChange={setTelemetryDialogOpen} />
      <CreateChainDialog open={createChainOpen} onOpenChange={setCreateChainOpen} />
      <CreateStoreDialog open={createStoreOpen} onOpenChange={setCreateStoreOpen} />
      <CreateUserDialog open={createUserOpen} onOpenChange={setCreateUserOpen} />
      <CreateVendorDialog open={createVendorOpen} onOpenChange={setCreateVendorOpen} />
      <CreateTransformationDialog open={createTransformationOpen} onOpenChange={setCreateTransformationOpen} />
      <CreateOctoRuleDialog open={createOctoRuleOpen} onOpenChange={setCreateOctoRuleOpen} />
      <CreateFirmwareDialog open={createFirmwareOpen} onOpenChange={setCreateFirmwareOpen} />
      <ImportDataDialog open={importDataOpen} onOpenChange={setImportDataOpen} />
      <CompareChainDialog defaultChainName="" open={compareChainOpen} onOpenChange={setCompareChainOpen} />
      <CompareStoreDialog defaultStoreName="" open={compareStoreOpen} onOpenChange={setCompareStoreOpen} />
    </>
  );
}
